import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function PageHeader({ title, subtitle, actions, icon: Icon }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 fade-in">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="mt-1 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Icon size={22} weight="duotone" />
          </div>
        )}
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1A1C18] leading-none">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "primary", testId }) {
  const accents = {
    primary: "text-primary bg-primary/10",
    terracotta: "text-[#C85A32] bg-[#C85A32]/10",
    ochre: "text-[#D69F3A] bg-[#D69F3A]/10",
    sky: "text-[#4A7C99] bg-[#4A7C99]/10",
  };
  return (
    <Card className="p-4 border-border rounded-lg hover:-translate-y-0.5 hover:shadow-md transition-transform bg-card" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && (
          <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accents[accent]}`}>
            <Icon size={18} weight="duotone" />
          </div>
        )}
      </div>
      <div className="mt-2 font-mono text-2xl font-semibold text-[#1A1C18]">{value}</div>
    </Card>
  );
}

const STATUS_STYLES = {
  Vacant: "bg-amber-100 text-amber-800 border-amber-200",
  Occupied: "bg-green-100 text-green-800 border-green-200",
  "Under Maintenance": "bg-orange-100 text-orange-800 border-orange-200",
  active: "bg-green-100 text-green-800 border-green-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  unpaid: "bg-red-100 text-red-800 border-red-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  terminated: "bg-red-100 text-red-800 border-red-200",
  expired: "bg-gray-100 text-gray-700 border-gray-200",
  payable: "bg-sky-100 text-sky-800 border-sky-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  prospective: "bg-sky-100 text-sky-800 border-sky-200",
  vacated: "bg-gray-100 text-gray-700 border-gray-200",
};

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-secondary text-secondary-foreground border-border";
  return (
    <Badge variant="outline" className={`capitalize font-medium ${cls}`}>
      {String(status || "").replace(/_/g, " ")}
    </Badge>
  );
}

export function EmptyState({ icon: Icon, title, message, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center fade-in">
      {Icon && (
        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
          <Icon size={28} weight="duotone" />
        </div>
      )}
      <h3 className="font-heading text-lg font-semibold text-[#1A1C18]">{title}</h3>
      {message && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}
